import React, { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { useCameraStream } from "../../../common/hooks/use-camera-stream";
import { CameraCodeReader } from "./camera-code-reader";
import { useCodeScannerOptions } from "../code-scanner/code-scanner-settings-context";
import { CodeScannerFormat } from "../code-scanner/code-scanner";

import "./style.scss";

interface ICameraScannerProps {
    formats: CodeScannerFormat[];
    onValueScan?: (value: string) => void;
}

export const CameraScanner = (props: ICameraScannerProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const scannerSettings = useCodeScannerOptions();
    const previousCode = useRef<string | undefined>(undefined);

    if (!scannerSettings.cameraDevicesList) {
        return <Fragment />;
    }

    const cameraDevice = useMemo(() => scannerSettings.preferredCameraDevice || scannerSettings.cameraDevicesList![0],
        [scannerSettings]);
    const { streamState, torchState } = useCameraStream(videoRef, cameraDevice.deviceId, scannerSettings.torchPreferred);

    useEffect(() => {
        if (streamState === "refused") {
            setTimeout(() => scannerSettings.onCameraAccessFailed(), 500);
        }
    }, [streamState]);

    useEffect(() => {
        scannerSettings.onCurrentCameraTorchStatusChange(torchState);
    }, [torchState]);

    const handleValueScan = useCallback((code: string) => {
        if (!props.onValueScan || previousCode.current === code) {
            return;
        }

        props.onValueScan(code);
        previousCode.current = code;
        setTimeout(() => previousCode.current = undefined, 3000);
    }, [props.onValueScan, previousCode.current]);

    return <Fragment>
        <video autoPlay={true} muted={true} playsInline={true} controls={false}
               className={`video-preview ${!props.onValueScan ? "video-preview--inactive" : ""}`} ref={videoRef} />
        {props.onValueScan && <CameraCodeReader formats={props.formats}
                                                videoSource={videoRef}
                                                onValueScan={handleValueScan} />}
    </Fragment>;
};
